import { BackIcon, ForwardIcon } from '@assets/icons';
import { Button } from '@components/button';
import { useGetGANModelsQuery } from '@generated/graphql';
import { Grid, Stack } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ResourcesType, TransferList, intersection, not } from './TransferList';

interface GanTransferListProps {
  organizationId: string;
  workspaceId: string;
  selectedGanModels: ResourcesType;
  setSelectedGanModels: Dispatch<SetStateAction<ResourcesType>>;
  setGanDirty: Dispatch<SetStateAction<boolean>>;
}

const getGANs = (
  gans: { modelId: string; name: string }[],
  selected: string[],
  available?: boolean,
) => {
  return (gans ?? [])
    .filter(({ modelId }) => (available ? !selected.includes(modelId) : selected.includes(modelId)))
    .map(({ modelId, name }) => ({ id: modelId, name }));
};

export const GanTransferList = ({
  organizationId,
  workspaceId,
  selectedGanModels,
  setSelectedGanModels,
  setGanDirty,
}: GanTransferListProps) => {
  const [checked, setChecked] = useState<ResourcesType>([]);
  const [right, setRight] = useState<ResourcesType>([]);
  const { data: availableGanModels, loading: availableGanModelsLoading } = useGetGANModelsQuery({
    variables: { organizationId },
  });
  const { data: includedGanModels, loading: includedGanModelsLoading } = useGetGANModelsQuery({
    variables: {
      workspaceId,
    },
  });
  const selectedGanModelsIds = (includedGanModels?.getGANModels ?? []).map((g) => g.modelId);

  useEffect(() => {
    if (!availableGanModelsLoading && !includedGanModelsLoading) {
      setRight(getGANs(availableGanModels?.getGANModels, selectedGanModelsIds, true));
      setSelectedGanModels(getGANs(availableGanModels?.getGANModels, selectedGanModelsIds, false));
    }

    return () => {
      setRight([]);
      setSelectedGanModels([]);
    };
    // Init-once when both loadings flip false; deliberately omits data deps
    // to avoid overwriting user edits on background refetches. WorkspaceResourcesModal
    // is slated for rework — revisit this pattern then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableGanModelsLoading, includedGanModelsLoading]);

  const leftChecked = intersection(checked, selectedGanModels);
  const rightChecked = intersection(checked, right);

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setSelectedGanModels(not(selectedGanModels, leftChecked));
    setGanDirty(true);
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setSelectedGanModels(selectedGanModels.concat(rightChecked));
    setRight(not(right, rightChecked));
    setGanDirty(true);
    setChecked(not(checked, rightChecked));
  };
  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={5}>
        <TransferList
          title="Included"
          items={selectedGanModels}
          checked={checked}
          setChecked={setChecked}
        />
      </Grid>
      <Grid item xs={2}>
        <Grid container direction="column" alignItems="center">
          <Stack spacing="16px">
            <Button
              variant="secondary"
              onClick={handleCheckedLeft}
              data-cy="Gan-Transfer-List-Add-Button"
              startIcon={BackIcon}
              disabled={rightChecked.length === 0}
            >
              Add
            </Button>
            <Button
              variant="secondary"
              data-cy="Gan-Transfer-List-Remove-Button"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              endIcon={ForwardIcon}
            >
              Remove
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <TransferList title="Excluded" items={right} checked={checked} setChecked={setChecked} />
      </Grid>
    </Grid>
  );
};
